import React, { useState } from 'react';

// Actions
import { ADD_PORTFOLIOS_TO_COMPARISON, addPortfoliosToComparison } from '@/redux/modules/comparison';
import { getPortfolioById } from '@/redux/modules/portfolios';
import { loadPortfolioForEdit, resetPortfolio, setPortfolioEditorContext } from '@/redux/modules/edit-portfolio';
import { saveContactPortfolio } from '@/redux/modules/contacts';
import { selectIsLoadingParamsEvery, selectIsLoadingSome, startLoaderAction, stopLoaderAction } from '@/redux/modules/ui/loaders';
import { togglePortfolioEditor } from '@/redux/modules/common/actionCreators';

// Components
import { FormattedMessageV2 } from '@/views/components/i18n';
import { DeletePortfoliosModal, EditPortfolioModal } from '@/views/components/modals';
import { LoadingSpinner } from '@/views/components/common';
import { SearchInput } from '@/views/components/inputs';
import { PortfoliosTableV2 } from '@/views/components/tables';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';

// Third party
import { ColumnSort } from '@tanstack/react-table';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

// Types
import { Household } from '@/types/households';
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS, HoldingAllocationMode } from '@/enums/Portfolio';
import { ReduxState } from '@/redux/modules/types';
import { SortingField } from '@/types/data';
import Dictionary from '@/types/dictionary';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';

// Utils
import {
  clonePortfolioAsProxy,
  getPortfolioMissingOrInvalidHoldings,
  loadPortfolioIfHoldingsAreMissing,
  loadPortfoliosIfHoldingsAreMissing,
} from '@/utils/portfolio/utils';
import { withLoaderAction } from '@/utils/promise';
import { DELETE_HOUSEHOLD_PORTFOLIOS, getHouseholdPortfolios } from '@/redux/modules/household';
import { formatMessage } from '@/utils/i18n';
import { getTableSortedColumns } from '@/utils/data-table';
import { NAV_PATHS } from '@/utils/constants';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '@/utils/permissions';
import { showError } from '@/utils/errors';
import { useDebouncedCallback } from 'use-debounce';
import disclaimers from '@/utils/disclaimers';
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import useRedirect from '@/hooks/useRedirect';
import { PortfolioEditorContext } from '@/utils/portfolios';

type MatchParams = {
  householdId: string;
};

type StateProps = {
  comparingPortfolioIds: string[];
  contact?: GenericContact;
  currentHousehold?: Household;
  editPortfolioModalIsVisible: boolean;
  hasMorePortfolios: boolean;
  isDeletingHouseholdPortfolio: boolean;
  loading: boolean;
  portfolios?: Portfolio[];
  user?: User | null;
};

type DispatchProps = {
  addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
  getPortfolioById: (
    id: string,
    portfolioType?: string,
    contact?: string,
    enterprise?: any,
    errorWhiteList?: string[],
    source?: Portfolio,
  ) => Promise<Portfolio>;
  getHouseholdPortfolios: (query?: string, sort?: SortingField[], shouldFetchNext?: boolean) => Promise<Portfolio[]>;
  loadPortfolioForEdit: (portfolio: Portfolio) => Promise<void>;
  resetPortfolio: () => void;
  saveContactPortfolio: (portfolio: Portfolio) => Promise<Portfolio>;
  startLoaderAction: (name: string) => void;
  stopLoaderAction: (name: string) => void;
  togglePortfolioEditor: (show: boolean) => void;
  setPortfolioEditorContext: (editorContext: PortfolioEditorContext) => void;
};

type Props = RouteComponentProps<MatchParams> & StateProps & DispatchProps;

const HouseholdPortfolios: React.FC<Props> = props => {
  const i18nEmptyStatePath = 'EmptyStateCard.Households.Portfolios';

  const [hasPendingComparison, setHasPendingComparison] = useState(false);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedPortfoliosForDelete, setSelectedPortfoliosForDelete] = useState<Portfolio[]>([]);
  const [showDeletePortfolioModal, setShowDeletePortfolioModal] = useState(false);
  const [sortingFields, setSortingFields] = useState<SortingField[]>([]);
  const [sortedColumns, setSortedColumns] = useState<ColumnSort[]>([]);

  const { redirect } = useRedirect();

  const { hasMorePortfolios, isDeletingHouseholdPortfolio, getHouseholdPortfolios } = props;

  // Actions
  const onCanEdit = (portfolio: Portfolio): boolean => {
    const { user } = props;
    return userHasPermission(user, PERMISSIONS.PORTFOLIOS_EDIT) && (!portfolio.availableActions || portfolio.availableActions.edit);
  };

  const onCanClone = (portfolio: Portfolio): boolean => {
    const { user } = props;
    return userHasPermission(user, PERMISSIONS.PORTFOLIOS_CLONE) && (!portfolio.availableActions || portfolio.availableActions.clone);
  };

  const onCanDelete = (portfolio: Portfolio): boolean => {
    const { user } = props;
    return userHasPermission(user, PERMISSIONS.PORTFOLIOS_DELETE) && (!portfolio.availableActions || portfolio.availableActions.delete);
  };

  const onComparePortfolios = async (portfolioList: Portfolio[]) => {
    const { addPortfoliosToComparison, getPortfolioById, startLoaderAction, stopLoaderAction, togglePortfolioEditor } = props;

    await withLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type, startLoaderAction, stopLoaderAction, async () => {
      const portfolios = await loadPortfoliosIfHoldingsAreMissing(portfolioList, getPortfolioById);

      const portfolioWithMissingHolding = portfolios.find(portfolio => disclaimers.portfolioHasMissingOrInvalidHoldings(portfolio));
      if (portfolioWithMissingHolding) {
        onEditPortfolio(portfolioWithMissingHolding, true);
        return;
      }

      await addPortfoliosToComparison(portfolios);
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      setHasPendingComparison(false);
      togglePortfolioEditor(false);

      redirect(NAV_PATHS.COMPARISON);
    });
  };

  const onDeletePortfolio = (portfolios: Portfolio[]) => {
    setSelectedPortfoliosForDelete(portfolios);
    setShowDeletePortfolioModal(true);
  };

  const onEditPortfolio = (inPortfolio: Portfolio, pendingComparison?: boolean) => {
    const { user, getPortfolioById, loadPortfolioForEdit } = props;

    props.setPortfolioEditorContext(PortfolioEditorContext.PORTFOLIO);

    loadPortfolioIfHoldingsAreMissing(inPortfolio, getPortfolioById)
      .then(portfolio => {
        const toLoad = disclaimers.portfolioHasMissingOrInvalidHoldings(portfolio) ? clonePortfolioAsProxy(portfolio) : portfolio;

        if (user && userHasPermissionEnabled(user, PERMISSIONS.PORTFOLIO_UNITS_MODE)) {
          toLoad.allocationMode = HoldingAllocationMode.Units;
        }

        loadPortfolioForEdit(toLoad)
          .then(() => {
            setHasPendingComparison(!!pendingComparison);
          })
          .catch(showError);
      })
      .catch(showError);
  };

  const onEditPortfolioSave = (portfolio: Portfolio): Promise<Portfolio> => {
    const { saveContactPortfolio, togglePortfolioEditor, resetPortfolio } = props;

    return saveContactPortfolio(portfolio).then((newPortfolio: Portfolio) => {
      resetPortfolio();
      if (hasPendingComparison) {
        onComparePortfolios([newPortfolio]);
      } else {
        togglePortfolioEditor(false);
      }
      getHouseholdPortfolios(query, sortingFields);
      return newPortfolio;
    });
  };

  const onCancelEditPortfolio = () => {
    const { togglePortfolioEditor, resetPortfolio } = props;
    resetPortfolio();
    togglePortfolioEditor(false);
  };

  const onSearch = (query: string) => {
    setQuery(query);
    setSearching(true);
    debouncedSearchPortfolios();
  };

  const onLoadMore = () => {
    getHouseholdPortfolios(query, sortingFields).then(() => {
      setSearching(false);
    });
  };

  const onCancelDeletePortfolio = () => {
    setShowDeletePortfolioModal(false);
  };

  const onConfirmDeletePortfolios = () => {
    // TODO: Implement delete portfolios
    setShowDeletePortfolioModal(false);
  };

  // Methods
  const debouncedSearchPortfolios = useDebouncedCallback(() => {
    getHouseholdPortfolios(query, sortingFields, false).then(() => {
      setSearching(false);
    });
  }, 250);

  const handleSorting = (sort: ColumnSort) => {
    const { serverSortingParams, columnSort } = getTableSortedColumns(sortingFields, sort);
    setSortingFields(serverSortingParams);
    setSortedColumns(columnSort);
    getHouseholdPortfolios(query, serverSortingParams, false);
  };

  const clearSearch = () => {
    setQuery('');
    debouncedSearchPortfolios();
  };

  // Table section
  const getDisplayedColumns = () => {
    const { user } = props;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.CONTACTS,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LOCK,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  const getDisplayedMetrics = () => [
    PORTFOLIO_METRICS_FIELDS.MER,
    PORTFOLIO_METRICS_FIELDS.YEAR_ONE,
    PORTFOLIO_METRICS_FIELDS.YEAR_THREE,
    PORTFOLIO_METRICS_FIELDS.SIZE,
    PORTFOLIO_METRICS_FIELDS.HOLDINGS,
  ];

  const getEmptyStateCard = (): JSX.Element => (
    <EmptyStateCard
      i18nVariables={{ householdName: props.currentHousehold?.name || '' }}
      messagePath={`${i18nEmptyStatePath}.Message`}
      titlePath={`${i18nEmptyStatePath}.Title`}
    />
  );

  // Header section
  const renderHeader = () => (
    <div className="household-portfolios-header">
      <div className="household-portfolios-title">
        <FormattedMessageV2 toLocalize="Households.Portfolios.Title" />
      </div>
      {!showEmptyCard && renderHeaderActions(searching, query)}
    </div>
  );

  const renderHeaderActions = (searching: boolean, query: string) => (
    <div className="household-portfolios-header-actions">
      <SearchInput
        className="search-input-v2"
        loading={searching}
        onChange={onSearch}
        placeholder={formatMessage('Households.Portfolios.SearchPlaceholder')}
        rightSection={query ? <i className="icon-cross-thin clear-term" onClick={clearSearch} /> : undefined}
        value={query}
      />
    </div>
  );

  // Table section
  const renderPortfolioTable = () => {
    const { comparingPortfolioIds, loading, portfolios } = props;

    return loading ? (
      <LoadingSpinner className="initial-loading" message={formatMessage('Contacts.ContactTabs.Portfolios.LoadingPortfolios')} />
    ) : (
      <PortfoliosTableV2<Portfolio>
        comparingPortfolioIds={comparingPortfolioIds}
        canEditPredicate={onCanEdit}
        canDeletePredicate={onCanDelete}
        canClonePredicate={onCanClone}
        displayedColumns={getDisplayedColumns()}
        displayedMetrics={getDisplayedMetrics()}
        onCompare={onComparePortfolios}
        onDelete={onDeletePortfolio}
        onEdit={onEditPortfolio}
        onLoadMore={onLoadMore}
        onServerSideSorting={sort => handleSorting(sort)}
        missingProductPredicate={portfolio => getPortfolioMissingOrInvalidHoldings(portfolio).length > 0}
        portfolios={portfolios}
        query={query}
        selectedTitle="Households.Portfolios.Title"
        serverSideSorting
        hasMore={hasMorePortfolios}
        sortedColumns={sortedColumns}
      />
    );
  };

  // Render section
  const { loading, portfolios, editPortfolioModalIsVisible } = props;
  const showEmptyCard = !loading && !searching && !query && !portfolios?.length;

  return (
    <div className="household-portfolios">
      {renderHeader()}
      <div className={`household-portfolios-body ${showEmptyCard ? 'full-height' : null}`}>{showEmptyCard ? getEmptyStateCard() : renderPortfolioTable()}</div>

      {editPortfolioModalIsVisible && <EditPortfolioModal show={editPortfolioModalIsVisible} onCancel={onCancelEditPortfolio} onSave={onEditPortfolioSave} />}

      <DeletePortfoliosModal
        isDeleting={isDeletingHouseholdPortfolio}
        onCancel={onCancelDeletePortfolio}
        onConfirm={onConfirmDeletePortfolios}
        portfolios={selectedPortfoliosForDelete}
        show={showDeletePortfolioModal}
      />
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const loadingParams = selectIsLoadingParamsEvery(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]);
  const comparingPortfolioIds = loadingParams.reduce((acc, param: Dictionary<unknown>) => {
    if (param.portfolios) {
      return [...acc, ...(param.portfolios as Portfolio[]).map(portfolio => portfolio._id!)];
    }

    return acc;
  }, [] as string[]);

  return {
    comparingPortfolioIds,
    currentHousehold: state.households.currentHousehold,
    editPortfolioModalIsVisible: state.comparison.portfolioEditor.isVisible,
    hasMorePortfolios: state.households.hasMore.portfolios,
    isDeletingHouseholdPortfolio: selectIsLoadingSome(state, [DELETE_HOUSEHOLD_PORTFOLIOS.type]),
    loading: state.households.loading.portfolios,
    portfolios: state.households.portfolios,
    user: state.auth.user,
  };
};

const mapDispatchToProps = {
  addPortfoliosToComparison,
  getHouseholdPortfolios,
  getPortfolioById,
  loadPortfolioForEdit,
  resetPortfolio,
  saveContactPortfolio,
  startLoaderAction,
  stopLoaderAction,
  togglePortfolioEditor,
  setPortfolioEditorContext,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(HouseholdPortfolios));
